import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionHeader from "../components/SectionHeader";

const faqs = [
  {
    question: "How quickly can I get started with Tekstation?",
    answer: "You can sign up and start using our platform immediately. Our onboarding process takes less than 5 minutes, and you'll have access to all core features. For enterprise deployments, our team typically has you fully operational within 24 hours."
  },
  {
    question: "What's included in the free trial?",
    answer: "The free trial includes full access to our core platform features: cloud infrastructure setup, basic data pipelines, 5 user accounts, and email support. No credit card required to start. You can upgrade anytime during or after your trial."
  },
  {
    question: "Can I integrate Tekstation with my existing tools?",
    answer: "Absolutely! Tekstation offers 500+ pre-built integrations with popular tools like Salesforce, Slack, Jira, and more. We also provide robust APIs and webhooks for custom integrations. Our integration marketplace makes connecting your existing stack seamless."
  },
  {
    question: "How does pricing work?",
    answer: "We offer flexible pricing plans: Starter ($299/month), Professional ($899/month), and Enterprise (custom). All plans include core platform features. Annual billing saves you 20%. Enterprise plans include dedicated infrastructure and custom development."
  },
  {
    question: "What kind of support do you provide?",
    answer: "All plans include email support. Professional plans add priority support with 24-hour response times. Enterprise plans include 24/7 phone support, dedicated account managers, and custom SLAs. We also offer extensive documentation and video tutorials."
  },
  {
    question: "Is my data secure with Tekstation?",
    answer: "Security is our top priority. We use bank-level encryption, are SOC 2 compliant, and maintain 99.99% uptime. All data is encrypted in transit and at rest. We also provide regular security audits and compliance reporting for enterprise customers."
  },
  {
    question: "Can I cancel or change plans anytime?",
    answer: "Yes, you have complete flexibility. You can upgrade, downgrade, or cancel your plan at any time with no penalties. Changes take effect at the next billing cycle. We also offer a 30-day money-back guarantee for all new customers."
  }
];

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section
      id="faq"
      className="section-padding"
      style={{ background: "var(--navy)" }}
      aria-labelledby="faq-title"
    >
      <div className="max-w-[1200px] mx-auto">
        <SectionHeader
          tag="Frequently Asked Questions"
          title="Got Questions?<br/>We've Got Answers"
          subtitle="Find answers to common questions about our platform, pricing, and features. Still need help? Our support team is here for you."
        />

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto mt-12"
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                duration: 0.5, 
                delay: index * 0.08,
                type: "spring",
                stiffness: 80,
                damping: 20
              }}
              className="mb-4"
            >
              <button
                className="w-full text-left p-6 rounded-2xl transition-all duration-300 hover:bg-gray-800/50"
                style={{
                  background: "rgba(15,31,61,0.6)",
                  border: "1px solid rgba(45,125,255,0.15)"
                }}
                onClick={() => toggleFAQ(index)}
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <div className="flex items-center justify-between">
                  <h3
                    className="font-display font-semibold text-lg text-white pr-4"
                    style={{ letterSpacing: "-0.01em" }}
                  >
                    {faq.question}
                  </h3>
                  <motion.div
                    animate={{ rotate: activeIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-blue-400 flex-shrink-0"
                  >
                    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7 7" />
                    </svg>
                  </motion.div>
                </div>
              </button>

              <AnimatePresence>
                {activeIndex === index && (
                  <motion.div
                    id={`faq-answer-${index}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="px-6 pb-6 text-gray-300 leading-7"
                    role="region"
                    aria-label="FAQ answer"
                  >
                    {faq.answer}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>

        {/* Still Need Help */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="inline-flex items-center gap-3 rounded-full px-6 py-3"
            style={{
              background: "rgba(26,107,255,0.1)",
              border: "1px solid rgba(26,107,255,0.3)"
            }}
          >
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-sm text-blue-400">Still have questions?</span>
          </div>
          <button
            className="btn-primary mt-4"
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
          >
            Contact Our Support Team
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
